import React, { useState } from "react";
import { entities } from "@/api/apiClient";
import { safeSplit } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Copy } from "lucide-react";

export default function SyncKanbanConfigPage() {
  const [emailOrigem, setEmailOrigem] = useState("jader.monte@fsbr.com.br");
  const [emailsDestino, setEmailsDestino] = useState("samuel.santos@fsbr.com.br, jader.monte@gmail.com");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSync = async () => {
    setLoading(true);
    setStatus(null);

    try {
      // Buscar todos os usuários
      const usuarios = await entities.User.list();
      
      // Encontrar usuário origem
      const usuarioOrigem = usuarios.find(u => u.email === emailOrigem.trim());
      if (!usuarioOrigem) {
        setStatus({ tipo: 'erro', mensagem: `Usuário origem "${emailOrigem}" não encontrado` });
        setLoading(false);
        return;
      }

      if (!usuarioOrigem.kanban_colunas || !Array.isArray(usuarioOrigem.kanban_colunas)) {
        setStatus({ tipo: 'erro', mensagem: `Usuário origem não possui configuração de kanban` });
        setLoading(false);
        return;
      }

      // Processar emails destino (API pode retornar undefined)
      const emailsArray = safeSplit(emailsDestino, ',').map((e) => e.trim()).filter(Boolean);
      const resultados = [];

      for (const emailDestino of emailsArray) {
        const usuarioDestino = usuarios.find(u => u.email === emailDestino);
        
        if (!usuarioDestino) {
          resultados.push({ email: emailDestino, sucesso: false, erro: 'Usuário não encontrado' });
          continue;
        }

        try {
          await entities.User.update(usuarioDestino.id, {
            kanban_colunas: usuarioOrigem.kanban_colunas
          });
          resultados.push({ email: emailDestino, sucesso: true });
        } catch (error) {
          resultados.push({ email: emailDestino, sucesso: false, erro: error.message });
        }
      }

      const sucessos = resultados.filter(r => r.sucesso).length;
      const falhas = resultados.filter(r => !r.sucesso).length;

      setStatus({
        tipo: falhas === 0 ? 'sucesso' : 'parcial',
        mensagem: `Sincronização concluída: ${sucessos} sucesso(s), ${falhas} falha(s)`,
        resultados
      });

    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      setStatus({ tipo: 'erro', mensagem: `Erro: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Sincronizar Configurações do Kanban
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="emailOrigem">Email do Usuário Origem</Label>
              <Input
                id="emailOrigem"
                type="email"
                value={emailOrigem}
                onChange={(e) => setEmailOrigem(e.target.value)}
                placeholder="usuario@exemplo.com"
              />
              <p className="text-sm text-slate-500">
                Configurações do Kanban serão copiadas deste usuário
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailsDestino">Emails dos Usuários Destino</Label>
              <Input
                id="emailsDestino"
                type="text"
                value={emailsDestino}
                onChange={(e) => setEmailsDestino(e.target.value)}
                placeholder="usuario1@exemplo.com, usuario2@exemplo.com"
              />
              <p className="text-sm text-slate-500">
                Separar múltiplos emails por vírgula
              </p>
            </div>

            <Button
              onClick={handleSync}
              disabled={loading || !emailOrigem || !emailsDestino}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Sincronizando...' : 'Sincronizar Configurações'}
            </Button>

            {status && (
              <Alert variant={status.tipo === 'erro' ? 'destructive' : 'default'}>
                {status.tipo === 'erro' ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
                <AlertDescription>
                  <p className="font-semibold mb-2">{status.mensagem}</p>
                  {status.resultados && (
                    <ul className="text-sm space-y-1 mt-2">
                      {status.resultados.map((r, idx) => (
                        <li key={idx} className={r.sucesso ? 'text-green-700' : 'text-red-700'}>
                          {r.email}: {r.sucesso ? '✓ Sucesso' : `✗ ${r.erro}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Atenção:</strong> Esta ação sobrescreverá as configurações do Kanban dos usuários destino. 
                Certifique-se de que os emails estão corretos antes de prosseguir.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}